import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Printer } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { formatDateTime, formatMoney } from '@/lib/format';
import { AgentTable, FunnelChart } from './Dashboards';
import { StatTile } from './StatTile';
import { exportToExcel, printReport, type ExportColumn } from './exportReport';
import {
  useAgentScorecard,
  useDailyActivity,
  useForecast,
  useFunnel,
  useLeakage,
  type ActivityRow,
  type AgentScorecard,
  type ForecastRow,
  type FunnelRow,
  type LeakageRow,
} from './useReports';

const TABS = ['funnel', 'scorecard', 'forecast', 'leakage', 'activity'] as const;
type Tab = (typeof TABS)[number];

const LEAK_TONE: Record<string, string> = {
  never_contacted: 'bg-red-100 text-red-900',
  stalled: 'bg-amber-100 text-amber-900',
  transferred_without_reason: 'bg-purple-100 text-purple-900',
  dropped_out_of_pipeline: 'bg-orange-100 text-orange-900',
};

export default function ReportsPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [exporting, setExporting] = useState(false);

  const tabParam = params.get('tab');
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'funnel';
  const setTab = (next: Tab) => {
    params.set('tab', next);
    setParams(params, { replace: true });
  };

  const funnel = useFunnel();
  const agents = useAgentScorecard();
  const forecast = useForecast();
  const leakage = useLeakage();
  const activity = useDailyActivity();

  const hours = (h: string) => Math.round(Number(h));

  // One export definition per report, so the sheet and the screen cannot drift.
  const exportCurrent = async () => {
    setExporting(true);
    try {
      if (tab === 'funnel') {
        const columns: ExportColumn<FunnelRow>[] = [
          {
            key: 'stage',
            header: t('reports.stage'),
            format: (r) => t(`stages.${r.stage}` as 'stages.new'),
          },
          { key: 'reached', header: t('reports.reached') },
          { key: 'currently_here', header: t('reports.currentlyHere') },
          { key: 'lost_here', header: t('reports.lostHere') },
          { key: 'pct_of_all', header: t('reports.pctOfAll') },
        ];
        await exportToExcel(funnel.data ?? [], columns, 'funnel', 'funnel');
      } else if (tab === 'scorecard') {
        const columns: ExportColumn<AgentScorecard>[] = [
          { key: 'agent_name', header: t('dashboard.cols.agent') },
          { key: 'leads', header: t('dashboard.cols.leads') },
          { key: 'contacted', header: t('dashboard.cols.contacted') },
          { key: 'qualified', header: t('dashboard.cols.qualified') },
          { key: 'visited', header: t('dashboard.cols.visited') },
          { key: 'sales', header: t('dashboard.cols.sales') },
          { key: 'revenue', header: t('dashboard.cols.revenue'), format: (r) => Number(r.revenue) },
          { key: 'sla_compliance_pct', header: t('dashboard.cols.sla') },
        ];
        await exportToExcel(agents.data ?? [], columns, 'scorecard', 'scorecard');
      } else if (tab === 'forecast') {
        const columns: ExportColumn<ForecastRow>[] = [
          {
            key: 'stage',
            header: t('reports.stage'),
            format: (r) => t(`stages.${r.stage}` as 'stages.new'),
          },
          { key: 'leads', header: t('reports.leads') },
          { key: 'probability', header: t('reports.probability') },
          {
            key: 'pipeline_value',
            header: t('reports.pipelineValue'),
            format: (r) => Number(r.pipeline_value),
          },
          {
            key: 'weighted_value',
            header: t('reports.weightedValue'),
            format: (r) => Number(r.weighted_value),
          },
        ];
        await exportToExcel(forecast.data ?? [], columns, 'forecast', 'forecast');
      } else if (tab === 'leakage') {
        const columns: ExportColumn<LeakageRow>[] = [
          { key: 'full_name', header: t('leads.fullName') },
          { key: 'phone_primary', header: t('leads.phone') },
          { key: 'owner_name', header: t('reports.owner') },
          {
            key: 'leak_type',
            header: t('reports.leakType'),
            format: (r) => t(`reports.leaks.${r.leak_type}` as 'reports.leaks.stalled'),
          },
          {
            key: 'hours_since',
            header: t('reports.hoursSince'),
            format: (r) => hours(r.hours_since),
          },
        ];
        await exportToExcel(leakage.data ?? [], columns, 'leakage', 'leakage');
      } else {
        const columns: ExportColumn<ActivityRow>[] = [
          { key: 'activity_date', header: t('reports.date') },
          { key: 'actor_name', header: t('dashboard.cols.agent') },
          { key: 'calls', header: t('leads.counts.calls') },
          { key: 'calls_answered', header: t('reports.answered') },
          { key: 'whatsapp', header: t('leads.counts.whatsapp') },
          { key: 'meetings', header: t('leads.counts.meetings') },
          { key: 'visits', header: t('leads.counts.visits') },
          { key: 'leads_touched', header: t('reports.leadsTouched') },
        ];
        await exportToExcel(activity.data ?? [], columns, 'activity', 'activity');
      }
    } finally {
      setExporting(false);
    }
  };

  const leaks = leakage.data ?? [];
  const leakCounts = leaks.reduce<Record<string, number>>((acc, l) => {
    acc[l.leak_type] = (acc[l.leak_type] ?? 0) + 1;
    return acc;
  }, {});

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <h1 className="text-xl font-bold text-slate-900">{t('nav.reports')}</h1>
        <div className="flex gap-2">
          <Button variant="secondary" loading={exporting} onClick={() => void exportCurrent()}>
            <Download className="size-4" aria-hidden="true" />
            {t('reports.exportExcel')}
          </Button>
          {/* The browser's own print-to-PDF: it shapes Arabic correctly, which no
              client-side PDF library in this stack does. */}
          <Button variant="ghost" onClick={printReport}>
            <Printer className="size-4" aria-hidden="true" />
            {t('reports.print')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200 print:hidden" role="tablist">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === name
                ? 'border-brand-700 text-brand-800'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t(`reports.tabs.${name}` as 'reports.tabs.funnel')}
          </button>
        ))}
      </div>

      {tab === 'funnel' ? (
        <>
          <p className="text-xs text-slate-500">{t('reports.funnelIntro')}</p>
          <FunnelChart />
        </>
      ) : null}

      {tab === 'scorecard' ? (
        agents.isPending ? (
          <Spinner />
        ) : (
          <AgentTable rows={agents.data} />
        )
      ) : null}

      {tab === 'forecast' ? (
        forecast.isPending ? (
          <Spinner />
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
              <StatTile
                label={t('reports.pipelineValue')}
                value={formatMoney(
                  (forecast.data ?? []).reduce((a, f) => a + Number(f.pipeline_value), 0),
                )}
              />
              <StatTile
                label={t('reports.weightedValue')}
                value={formatMoney(
                  (forecast.data ?? []).reduce((a, f) => a + Number(f.weighted_value), 0),
                )}
                tone="good"
              />
              <StatTile
                label={t('reports.openLeads')}
                value={(forecast.data ?? []).reduce((a, f) => a + f.leads, 0)}
              />
            </div>
            <p className="text-xs text-slate-500">{t('reports.forecastIntro')}</p>
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-start">{t('reports.stage')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.leads')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.probability')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.pipelineValue')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.weightedValue')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(forecast.data ?? []).map((row) => (
                    <tr key={row.stage} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-900">
                        {t(`stages.${row.stage}` as 'stages.new')}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700">
                        {row.leads}
                        {row.leads_without_value > 0 ? (
                          <span className="ms-1 text-xs text-amber-700">
                            ({t('reports.noBudget', { count: row.leads_without_value })})
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700" dir="ltr">
                        {Math.round(Number(row.probability) * 100)}%
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                        {formatMoney(row.pipeline_value)}
                      </td>
                      <td
                        className="whitespace-nowrap px-3 py-2 font-medium text-slate-900"
                        dir="ltr"
                      >
                        {formatMoney(row.weighted_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {tab === 'leakage' ? (
        leakage.isPending ? (
          <Spinner />
        ) : leaks.length === 0 ? (
          <Alert tone="success">{t('reports.noLeaks')}</Alert>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {(
                [
                  'never_contacted',
                  'stalled',
                  'transferred_without_reason',
                  'dropped_out_of_pipeline',
                ] as const
              ).map((type) => (
                <StatTile
                  key={type}
                  label={t(`reports.leaks.${type}` as 'reports.leaks.stalled')}
                  value={leakCounts[type] ?? 0}
                  tone={(leakCounts[type] ?? 0) > 0 ? 'bad' : 'good'}
                />
              ))}
            </div>

            <p className="text-xs text-slate-500">{t('reports.leakageIntro')}</p>

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2 text-start">{t('leads.fullName')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.owner')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.leakType')}</th>
                    <th className="px-3 py-2 text-start">{t('reports.hoursSince')}</th>
                    <th className="px-3 py-2 text-start">{t('leads.lastActivity')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {leaks.map((row) => (
                    <tr key={`${row.lead_id}-${row.leak_type}`} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <Link
                          to={`/leads/${row.lead_id}`}
                          className="font-medium text-brand-800 hover:underline"
                        >
                          {row.full_name}
                        </Link>
                        <div className="text-xs text-slate-500" dir="ltr">
                          {row.phone_primary}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{row.owner_name ?? '—'}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            LEAK_TONE[row.leak_type] ?? 'bg-slate-100 text-slate-700'
                          }`}
                        >
                          {t(`reports.leaks.${row.leak_type}` as 'reports.leaks.stalled')}
                        </span>
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700" dir="ltr">
                        {hours(row.hours_since)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-600">
                        {row.last_activity_at ? formatDateTime(row.last_activity_at) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      ) : null}

      {tab === 'activity' ? (
        activity.isPending ? (
          <Spinner />
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-start">{t('reports.date')}</th>
                  <th className="px-3 py-2 text-start">{t('dashboard.cols.agent')}</th>
                  <th className="px-3 py-2 text-start">{t('leads.counts.calls')}</th>
                  <th className="px-3 py-2 text-start">{t('reports.answered')}</th>
                  <th className="px-3 py-2 text-start">{t('leads.counts.whatsapp')}</th>
                  <th className="px-3 py-2 text-start">{t('leads.counts.meetings')}</th>
                  <th className="px-3 py-2 text-start">{t('leads.counts.visits')}</th>
                  <th className="px-3 py-2 text-start">{t('reports.leadsTouched')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(activity.data ?? []).slice(0, 100).map((row) => (
                  <tr key={`${row.activity_date}-${row.actor_id}`} className="hover:bg-slate-50">
                    <td className="whitespace-nowrap px-3 py-2 text-slate-700" dir="ltr">
                      {row.activity_date}
                    </td>
                    <td className="px-3 py-2 text-slate-900">{row.actor_name}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{row.calls}</td>
                    <td className="px-3 py-2 tabular-nums text-green-700">{row.calls_answered}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{row.whatsapp}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{row.meetings}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{row.visits}</td>
                    <td className="px-3 py-2 tabular-nums text-slate-700">{row.leads_touched}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}
    </section>
  );
}
